"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";

type Props = {
  src: string;
  alt: string;
  unoptimized?: boolean;
  /** CSS `object-position` value (e.g. "center", "top", "50% 25%"). */
  objectPosition?: string;
};

/**
 * Hero image with a subtle scroll-linked parallax: as the page scrolls,
 * the image translates at ~70% scroll speed (i.e. lags the foreground by
 * ~30%), giving the hero a sense of depth without obvious motion.
 *
 * Implementation: the container is `overflow-hidden`; the image layer
 * inside is positioned with 15% overflow above and below so a clamped
 * `translate3d` never reveals the container's background. Updates are
 * throttled to one rAF per scroll burst, and the effect is disabled
 * entirely when the user has `prefers-reduced-motion: reduce`.
 */
export function ParallaxHero({ src, alt, unoptimized, objectPosition }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf: number | null = null;
    const update = () => {
      raf = null;
      const c = containerRef.current;
      const l = layerRef.current;
      if (!c || !l) return;
      const rect = c.getBoundingClientRect();
      // Image moves at ~70% of scroll speed (lag = 30%). Clamp inside
      // the layer's 15% overflow on each side so we never expose the
      // container background.
      const max = c.clientHeight * 0.15;
      const ty = Math.max(-max, Math.min(max, -rect.top * 0.3));
      l.style.transform = `translate3d(0, ${ty}px, 0)`;
    };
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative h-[50svh] min-h-[320px] w-full overflow-hidden sm:h-[60svh] sm:min-h-[420px]"
    >
      <div
        ref={layerRef}
        className="absolute inset-x-0 top-[-15%] bottom-[-15%] will-change-transform"
      >
        <Image
          src={src}
          alt={alt}
          fill
          priority
          unoptimized={unoptimized}
          sizes="100vw"
          className="object-cover"
          style={{ objectPosition: objectPosition ?? "center" }}
        />
      </div>
    </div>
  );
}
